import { campaignKindOf } from '@/lib/campaign-status';
// EncorePro Pipeline — productions-driven monitor. One row per production (AS IS
// from the productions list), overlaid with batch-computed 30-day stats parsed
// from sam.tickets (D1 `production_stats_daily`, via /pipeline/stats). Clicking a
// production opens a daily breakdown (30-day charts + a day-by-day table) from
// /pipeline/stats/{id}. A manual backfill (date range ≤90d + optional production)
// re-parses tickets and refills the stats.
//
// "Should be selling" is run-date based. The opening-soon and stall windows
// come from GET /pipeline/policy; quality bands come from GET /health/metrics.
// If either policy read is unavailable the UI withholds classification instead
// of silently falling back to copied browser thresholds.

import { ResponsiveKpis } from '@/components/responsive-kpis';
import { ResponsiveOverlay } from '@/components/responsive-overlay';
import { ResponsiveFilters } from '@/components/responsive-filters';
import { ScrollTable } from '@/components/scroll-table';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Icons, fmt, MoneyCell } from '@/components/primitives';
import { AxisChart, MetricChart, CoverageChart } from '@/components/charts';
import { Dropdown, SearchableDropdown, DateFilter, fmtInput } from '@/components/dropdowns';
import type { DateFilterValue } from '@/components/dropdowns';
import { CPPanel, CPSparkBtn, useExplainPanel } from '@/components/copilot';
import { PosterChip, ChannelGlyphs } from '@/screens/productions';
import { api } from '@/api/client';
import { useRegisteredMutation } from '@/api/mutation';
import { useCan } from '@/auth/capabilities';
import { bandColor, bandFor, useSystemMetric } from '@/lib/system-metrics';
import type { Bandable } from '@/lib/system-metrics';
import type {
  Campaign,
  EnqueueBackfill,
  IngestCorrection,
  IngestRun,
  PipelineMonitorPolicy,
  Production,
  ProductionStat,
  ProductionStatDay,
  ReportByProductionRow,
  Venue,
} from '@/api/types';
import { MutationErrorLine, QueryErrorNote } from '@/components/error-panel';
import { RequiredField, useRequiredFeedback } from '@/components/required-field';
import { downloadCsv, csvFilename } from '@/lib/csv';
import { productionExplainRequest, runExplainRequest } from '@/lib/copilot';
import { venueOptionLabel } from '@/lib/venue-label';
import { fmtDay, fmtDayShort, fmtDateShort, fmtDateTime, nyParts, nyMidnightMs, nyToday, dayYmd } from '@/lib/datetime';
import {
  useProductions, useIngestRuns, usePipelineCorrections,
  usePipelineStats, usePipelineStatDetail, usePipelineMonitorPolicy, useSystemHealth,
  useVenues, useCampaigns, useReportingByProduction, useCopilotStatus,
} from '@/api/hooks';
import { PageActions } from '@/components/page-actions';

const DAY_MS = 86400000;

/** A server-delivered quality threshold, as `/health/metrics` returns it. */
type QualityMetric = Bandable & { stale: boolean };

/** The catalogued EncorePro quality metrics this screen bands against. */
interface QualityPolicy {
  identity?: QualityMetric;
  refund?: QualityMetric;
  anonymous?: QualityMetric;
  missingEmail?: QualityMetric;
  nullVenue?: QualityMetric;
  nullEvent?: QualityMetric;
  missingValue?: QualityMetric;
  missingCurrency?: QualityMetric;
}

/** The server's monitor policy; `null`/`undefined` while unknown, which withholds every verdict. */
type MonitorPolicy = PipelineMonitorPolicy | null | undefined;

/** One table row: every production, left-joined to its 30-day stat. */
interface PipelineRow {
  production: Production;
  stat: ProductionStat | undefined;
}

/** Portfolio coverage percentages across the monitored set; `null` when nothing sold. */
interface AvgCoverage {
  email: number | null;
  phone: number | null;
  address: number | null;
}

// ─── helpers ─────────────────────────────────────────────────────────────────
function pct(ct: number, total: number): number { return total ? (Number(ct) / Number(total)) * 100 : 0; }
/** The tone of a cell no catalogued metric bands. */
const NEUTRAL = 'var(--ink-2)';
// A cell banded against its server-delivered metric. Only catalogued values are
// coloured: an uncatalogued or stale metric stays neutral (no local fallback),
// so a cell can never disagree with an alert. Callers pass the value the metric
// itself measures: the email cell shows coverage but bands the missing-email
// rate, exactly as `qualityFlags` does.
function metricColor(value: number, metric: QualityMetric | undefined): string {
  if (!metric || metric.stale) return NEUTRAL;
  return bandColor(bandFor(metric, value, 'unknown'));
}
function relDay(ms: number | null | undefined): string {
  if (ms == null) return 'never';
  // last_active_day is a calendar-day epoch (00:00:00Z); anchor "today" to the
  // NY calendar day so the boundary matches the operator's wall clock.
  const t = nyParts(Date.now());
  const todayIdx = Math.floor(Date.UTC(t.y, t.m - 1, t.d) / DAY_MS);
  const d = todayIdx - Math.floor(Number(ms) / DAY_MS);
  if (d <= 0) return 'today';
  if (d === 1) return 'yesterday';
  if (d < 30) return d + 'd ago';
  return '30d+ ago';
}
// Day rendering now comes from '@/lib/datetime': fmtDay/fmtDayShort for
// calendar-day epochs (c.day/d.day/window_from/window_to), fmtDateShort for the
// instant `first_event_starts_at`.

// ─── run-date "monitored" logic (the keystone) ───────────────────────────────
function startMs(p: Production): number | null { return p.first_event_starts_at ? new Date(p.first_event_starts_at).getTime() : null; }
function endMs(p: Production): number | null { return p.last_event_starts_at ? new Date(p.last_event_starts_at).getTime() : null; }
function isRunning(p: Production): boolean { const s = startMs(p), e = endMs(p), n = Date.now(); return s != null && e != null && n >= s && n <= e; }
function isUpcomingSoon(p: Production, monitorPolicy: MonitorPolicy): boolean {
  if (!monitorPolicy) return false;
  const s = startMs(p), n = Date.now();
  return s != null && s > n && s <= n + Number(monitorPolicy.upcoming_window_days) * DAY_MS;
}
function isMonitored(p: Production, monitorPolicy: MonitorPolicy): boolean { return isRunning(p) || isUpcomingSoon(p, monitorPolicy); }
type StallKind = 'stalled' | 'no_presales';
// Distinct broken-pipeline failure modes for a production that MUST be selling.
function stallKind(p: Production, stat: ProductionStat | undefined, monitorPolicy: MonitorPolicy): StallKind | null {
  if (!monitorPolicy) return null;
  if (isRunning(p) && (stat?.last_active_day == null || (Date.now() - Number(stat.last_active_day)) > Number(monitorPolicy.stall_after_days) * DAY_MS)) return 'stalled';
  if (isUpcomingSoon(p, monitorPolicy) && !(stat && stat.tickets > 0)) return 'no_presales';
  return null;
}

function metricBreach(metric: QualityMetric | undefined, value: number): boolean | null {
  if (!metric || metric.stale || !Number.isFinite(value)) return null;
  return bandFor(metric, value, 'unknown') !== 'ok';
}

// A run's liveness verdict. Tri-state on purpose: without the server policy we
// know nothing about the heartbeat, and "unknown" must not be rendered as the
// alarm state "stale" — that would flag every running run while /pipeline/policy
// is still in flight.
type HeartbeatState = 'n/a' | 'unknown' | 'missing' | 'fresh' | 'stale';
function heartbeatState(r: IngestRun, monitorPolicy: MonitorPolicy): HeartbeatState {
  if (r.status !== 'running') return 'n/a';
  if (!monitorPolicy) return 'unknown';
  if (r.heartbeat_at == null) return 'missing';
  return Date.now() - Number(r.heartbeat_at) <= Number(monitorPolicy.run_heartbeat_stale_secs) * 1000
    ? 'fresh'
    : 'stale';
}

// Flag a production from its run-status + 30-day aggregate. counts → % over `tickets`.
// Each display group carries its OWN unknown flag: the References cell must not
// claim "thresholds unavailable" because an unrelated money metric went stale.
interface QualityFlags {
  identity: boolean;
  refs: boolean;
  money: boolean;
  identityUnknown: boolean;
  refsUnknown: boolean;
  moneyUnknown: boolean;
  policyUnknown: boolean;
  stall: boolean;
  stallKind: StallKind | null;
  count: number;
}
function qualityFlags(
  p: Production,
  s: ProductionStat | undefined,
  qualityPolicy: QualityPolicy,
  monitorPolicy: MonitorPolicy,
): QualityFlags {
  const kind = stallKind(p, s, monitorPolicy);
  const stall = kind != null;
  if (!s || !s.tickets) {
    return {
      identity: false, refs: false, money: false,
      identityUnknown: false, refsUnknown: false, moneyUnknown: false,
      policyUnknown: false, stall, stallKind: kind, count: stall ? 1 : 0,
    };
  }
  const t = s.tickets;
  const checks = {
    anonymous: metricBreach(qualityPolicy?.anonymous, pct(s.anon_ct, t)),
    email: metricBreach(qualityPolicy?.missingEmail, pct(t - s.email_ct, t)),
    venue: metricBreach(qualityPolicy?.nullVenue, pct(s.null_venue_ct, t)),
    event: metricBreach(qualityPolicy?.nullEvent, pct(s.null_event_ct, t)),
    refund: metricBreach(qualityPolicy?.refund, pct(s.refunds, t)),
    value: metricBreach(qualityPolicy?.missingValue, pct(s.missing_value_ct, t)),
    currency: metricBreach(qualityPolicy?.missingCurrency, pct(s.missing_currency_ct, t)),
  };
  const identity = checks.anonymous === true || checks.email === true;
  const refs = checks.venue === true || checks.event === true;
  const money = checks.refund === true || checks.value === true || checks.currency === true;
  const identityUnknown = checks.anonymous == null || checks.email == null;
  const refsUnknown = checks.venue == null || checks.event == null;
  const moneyUnknown = checks.refund == null || checks.value == null || checks.currency == null;
  const policyUnknown = identityUnknown || refsUnknown || moneyUnknown;
  const count = [identity, refs, money, stall].filter(Boolean).length;
  return {
    identity, refs, money,
    identityUnknown, refsUnknown, moneyUnknown,
    policyUnknown, stall, stallKind: kind, count,
  };
}

// ─── charts ──────────────────────────────────────────────────────────────────
// AxisChart / MetricChart / CoverageChart moved to components/charts.tsx
// (shared with the Data Pipeline screen); re-exported below unchanged.

interface IdentityCellProps {
  s: ProductionStat | undefined;
  qualityPolicy: QualityPolicy;
}

function IdentityCell({ s, qualityPolicy }: IdentityCellProps) {
  if (!s || !s.tickets) return <span className="muted">—</span>;
  const email = Math.round(pct(s.email_ct, s.tickets));
  const phone = Math.round(pct(s.phone_ct, s.tickets));
  const address = Math.round(pct(s.tickets - (Number(s.missing_address_ct) || 0), s.tickets));
  // Email is banded on the catalogued missing-email rate (the value the metric measures);
  // phone and address have no catalogued metric and stay neutral.
  const rows = [
    { lbl: 'E', name: 'Email', v: email, color: metricColor(pct(s.tickets - s.email_ct, s.tickets), qualityPolicy.missingEmail) },
    { lbl: 'P', name: 'Phone', v: phone, color: NEUTRAL },
    { lbl: 'A', name: 'Address', v: address, color: NEUTRAL },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 120 }}>
      {rows.map(({ lbl, name, v, color }) => (
        <div key={lbl} title={`${name}: ${v}%`} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 8.5, color: 'var(--ink-3)', width: 11, flexShrink: 0 }}>{lbl}</span>
          <div style={{ flex: 1, height: 4, background: 'var(--bg-sunk)', borderRadius: 1, overflow: 'hidden' }}>
            <div style={{ width: v + '%', height: '100%', background: color }}/>
          </div>
          <span className="mono" style={{ fontSize: 9.5, width: 26, textAlign: 'right', color }}>{v}%</span>
        </div>
      ))}
    </div>
  );
}

interface QualityChipProps {
  p: Production;
  s: ProductionStat | undefined;
  qualityPolicy: QualityPolicy;
  monitorPolicy: MonitorPolicy;
}

// Quality / health chip for a row, leading with the broken-pipeline alert.
function QualityChip({ p, s, qualityPolicy, monitorPolicy }: QualityChipProps) {
  const flags = qualityFlags(p, s, qualityPolicy, monitorPolicy);
  if (flags.stallKind === 'stalled') return <span className="mono" style={{ fontSize: 11, fontWeight: 700, color: 'var(--bad)' }}>● STALLED</span>;
  if (flags.stallKind === 'no_presales') return <span className="mono" style={{ fontSize: 11, fontWeight: 700, color: 'var(--bad)' }}>● NO PRESALES</span>;
  if (s && s.tickets) {
    if (flags.count > 0) {
      return <span className="mono" style={{ fontSize: 11, fontWeight: 700, color: 'var(--bad)' }}>● {flags.count} issue{flags.count === 1 ? '' : 's'}{flags.policyUnknown ? ' · partial' : ''}</span>;
    }
    if (flags.policyUnknown) return <span className="muted mono" style={{ fontSize: 10.5 }}>thresholds unavailable</span>;
    return <span className="mono" style={{ fontSize: 11, fontWeight: 700, color: 'var(--good)' }}>● clean</span>;
  }
  return <span className="muted" style={{ fontStyle: 'italic', fontSize: 11 }}>no data</span>;
}

// ─── KPI strip ───────────────────────────────────────────────────────────────
interface PipelineKpiStripProps {
  rows: PipelineRow[];
  qualityPolicy: QualityPolicy;
  monitorPolicy: MonitorPolicy;
  statsReady: boolean;
}

/** The summed stat columns the KPI strip reads. */
type SummedStat = 'tickets' | 'refunds' | 'revenue_cents' | 'email_ct' | 'household_reach_ct' | 'anon_ct' | 'person_resolved_ct';

/** label, value, sub-line, and an optional bar under it. */
type KpiCell = [label: string, value: ReactNode, sub?: ReactNode, extra?: ReactNode];

function PipelineKpiStrip({ rows, qualityPolicy, monitorPolicy, statsReady }: PipelineKpiStripProps) {
  const idM = qualityPolicy?.identity;
  const refM = qualityPolicy?.refund;
  const n = rows.length;
  const running = rows.filter(m => isRunning(m.production)).length;
  const upcoming = rows.filter(m => !isRunning(m.production) && isUpcomingSoon(m.production, monitorPolicy)).length;
  const other = n - running - upcoming;
  const monitoredRows = rows.filter(m => isMonitored(m.production, monitorPolicy));
  // A stall is read off the stats: until they have loaded, a missing row means "not loaded",
  // never "no tickets", so the count is withheld rather than shown as every running production.
  const broken = monitoredRows.filter(m => stallKind(m.production, m.stat, monitorPolicy) != null).length;
  const monitoredPct = n ? (monitoredRows.length / n) * 100 : 0;
  const monitorPolicyReady = !!monitorPolicy && statsReady;

  const sum = (f: SummedStat) => rows.reduce((a, m) => a + (m.stat ? Number(m.stat[f]) || 0 : 0), 0);
  const tickets = sum('tickets');
  const refunds = sum('refunds');
  const revenue = sum('revenue_cents') / 100;
  const emailCt = sum('email_ct');
  const households = sum('household_reach_ct');
  // Identity coverage = resolved person_ids over records that HAD PII
  // (tickets - anonymous), matching the encore.identity_coverage_pct alert.
  const piiRecords = tickets - sum('anon_ct');
  const identityPct = piiRecords > 0 ? Math.round((sum('person_resolved_ct') / piiRecords) * 100) : 0;
  const refundPct = tickets ? Math.round((refunds / tickets) * 100) : 0;
  const brokenColor = !monitorPolicyReady ? 'var(--ink-3)' : broken === 0 ? 'var(--good)' : 'var(--bad)';
  const idBand = idM && !idM.stale ? bandFor(idM, identityPct, 'unknown') : 'unknown';
  const refBand = refM && !refM.stale ? bandFor(refM, refundPct, 'unknown') : 'unknown';
  const upcomingDays = monitorPolicy?.upcoming_window_days;

  const cells: KpiCell[] = [
    ['Productions', fmt.nKpi(n),
      <span style={{ color: 'var(--ink-3)' }}>{running} running · {upcoming} opening {upcomingDays ? `≤${upcomingDays}d` : '(policy unavailable)'} · {other} other</span>,
      monitorPolicyReady ? <div style={{ height: 3, background: 'var(--bg-sunk)', borderRadius: 1, overflow: 'hidden', marginTop: 6 }}><div style={{ width: monitoredPct + '%', height: '100%', background: 'var(--good)' }}/></div> : null],
    ['Tickets · 30d', fmt.nKpi(tickets), <span style={{ color: 'var(--ink-3)' }}>{fmt.n(tickets - refunds)} sales · {fmt.n(refunds)} refunds</span>],
    ['Revenue · 30d', <MoneyCell value={revenue} dp={2}/>],
    ['Identity coverage', identityPct + '%', <span style={{ color: bandColor(idBand) }}>{idBand === 'ok' ? 'on target' : idBand === 'warn' ? 'below target' : idBand === 'critical' ? 'investigate' : 'threshold unavailable'}</span>],
    ['Households · 30d', fmt.nKpi(households), <span style={{ color: 'var(--ink-3)' }}>distinct households reached</span>],
    ['Broken pipelines', monitorPolicyReady ? fmt.nKpi(broken) : '—', <span style={{ color: brokenColor }}>● {!monitorPolicy ? 'monitor policy unavailable' : !statsReady ? 'stats unavailable' : broken === 0 ? 'all flowing' : `${broken} need attention`}</span>],
    ['Refund rate', refundPct + '%', <span style={{ color: bandColor(refBand) }}>{refBand === 'ok' ? 'normal' : refBand === 'unknown' ? 'threshold unavailable' : 'elevated'}</span>],
  ];
  return (
    <ResponsiveKpis><div className="kpi-strip" style={{ gridTemplateColumns: 'repeat(7, 1fr)' }}>
      {cells.map(([key, v, sub, extra]) => (
        <div className="kpi" key={key}>
          <div className="kpi-label">{key}</div>
          <div className="kpi-val">{v}</div>
          {sub && <div className="kpi-sub" style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink-3)', marginTop: 2 }}>{sub}</div>}
          {extra}
        </div>
      ))}
    </div></ResponsiveKpis>
  );
}

// ─── detail drawer (charts + daily table) ────────────────────────────────────
function tile(label: string, value: ReactNode) {
  return (
    <div>
      <div className="muted mono" style={{ fontSize: 9.5, letterSpacing: '.08em', textTransform: 'uppercase' }}>{label}</div>
      <div className="mono" style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function chartHd(title: string, note?: string) {
  return (
    <div className="field-lbl mb-2">{title}{note ? <span className="muted" style={{ textTransform: 'none', letterSpacing: 0 }}> {note}</span> : null}</div>
  );
}

interface PipelineDetailDrawerProps {
  production: Production;
  /** The production's venue by name, as its row shows it; null when unknown. */
  venueName: string | null;
  stat: ProductionStat | undefined;
  avgCoverage: AvgCoverage;
  qualityPolicy: QualityPolicy;
  onClose: () => void;
}

function PipelineDetailDrawer({ production, venueName, stat, avgCoverage, qualityPolicy, onClose }: PipelineDetailDrawerProps) {
  const { data: days = [], isLoading } = usePipelineStatDetail(production.id);
  const { data: corrections = [] } = usePipelineCorrections(production.id);
  // The newest few by default, and every one on request: the list is complete.
  const [allCorrections, setAllCorrections] = useState(false);
  const shownCorrections = allCorrections ? corrections : corrections.slice(0, CORRECTIONS_PREVIEW);
  const copilotOn = useCopilotStatus().data?.enabled === true;
  const ex = useExplainPanel();
  // Most-recent correction per day (corrections arrive newest-first), for the
  // daily-table badges + the corrections panel.
  const corrByDay = new Map<number, IngestCorrection>();
  for (const c of corrections) { const k = Number(c.day); if (!corrByDay.has(k)) corrByDay.set(k, c); }
  // Dense 30-day series (UTC) so chart gaps are visible; table uses active days.
  const todayUtc = Math.floor(Date.now() / DAY_MS) * DAY_MS;
  const byDay = new Map<number, ProductionStatDay>(days.map(d => [Number(d.day), d]));
  const denseDays = Array.from({ length: 30 }, (_, i) => todayUtc - (29 - i) * DAY_MS);
  const dense = denseDays.map(ms => byDay.get(ms) ?? null);
  const dayLabels = denseDays.map(ms => fmtDayShort(ms));

  const totalsSeries = dense.map(d => (d ? Number(d.tickets) || 0 : 0));
  const ticketsSeries = totalsSeries;
  const revenueSeries = dense.map(d => (d ? (Number(d.revenue_cents) || 0) / 100 : 0));
  const emailCounts = dense.map(d => (d ? Number(d.email_ct) || 0 : 0));
  const phoneCounts = dense.map(d => (d ? Number(d.phone_ct) || 0 : 0));
  const addrCounts = dense.map(d => (d ? Math.max((Number(d.tickets) || 0) - (Number(d.missing_address_ct) || 0), 0) : 0));
  // `counts` is index-aligned with `totalsSeries` (both map `dense`), so `?? 0` never applies.
  const coverageSeries = (counts: number[]) => totalsSeries.map((t, i) => (t ? pct(counts[i] ?? 0, t) : 0));
  const emailSeries = coverageSeries(emailCounts);
  const phoneSeries = coverageSeries(phoneCounts);
  const addrSeries = coverageSeries(addrCounts);
  const activeDesc = [...days].reverse();

  const t = stat?.tickets || 0;
  const peakT = Math.max(...ticketsSeries, 0);
  const peakR = Math.max(...revenueSeries, 0);
  const emailPct30 = stat && t ? Math.round(pct(stat.email_ct, t)) : 0;
  const phonePct30 = stat && t ? Math.round(pct(stat.phone_ct, t)) : 0;
  const addrPct30 = stat && t ? Math.round(pct(t - (Number(stat.missing_address_ct) || 0), t)) : 0;
  const houseNote = (key: keyof AvgCoverage) => {
    const avg = avgCoverage[key];
    return avg != null ? ` · house avg ${Math.round(avg)}%` : '';
  };

  return (
    <>

      <ResponsiveOverlay label="pipeline details" onClose={onClose} className="drawer">
        <div className="drawer-hd">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="muted mono" style={{ fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase' }}>EncorePro Pipeline · <span style={{ color: 'var(--ink-1)' }}>{production.id}</span></div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 800, letterSpacing: '.01em', margin: '4px 0 6px', textTransform: 'uppercase' }}>{production.name}</h2>
            <span className="muted mono" style={{ fontSize: 10.5, letterSpacing: '.06em' }}>{venueName || '—'} · {fmt.n(t)} tickets / 30d · last activity {relDay(stat?.last_active_day)}</span>
          </div>
          <button className="btn btn-sm btn-ghost" onClick={onClose} style={{ fontSize: 18, padding: '4px 10px' }}>×</button>
        </div>

        <div className="drawer-body">
          <div className="field-lbl mb-2">At a glance · last 30 days</div>
          <div className="panel" style={{ padding: 12, marginBottom: 14 }}>
            <div className="pair-phone" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
              {tile('Tickets', fmt.n(t))}
              {tile('Refunds', fmt.n(stat?.refunds || 0))}
              {tile('Revenue', <MoneyCell value={(Number(stat?.revenue_cents) || 0) / 100}/>)}
              {tile('Households', fmt.n(stat?.household_reach_ct || 0))}
              {tile('Active days', `${stat?.active_days || 0} / 30`)}
            </div>
          </div>

          {copilotOn && (
            <div style={{ marginBottom: 14 }}>
              <CPSparkBtn label="Explain this production" open={ex.open}
                onClick={() => (ex.open ? ex.close() : ex.trigger(productionExplainRequest(production.id)))}/>
              {ex.open && (
                <div className="cp-dock-in-card" style={{ marginTop: 8 }}>
                  <CPPanel resp={ex.resp} state={ex.state} kind="production" error={ex.error}
                    onClose={ex.close} onRegenerate={ex.regenerate} regenBusy={ex.state === 'loading'}/>
                </div>
              )}
            </div>
          )}

          {corrections.length > 0 && (
            <>
              <div className="field-lbl mb-2">Data corrections <span className="muted" style={{ textTransform: 'none', letterSpacing: 0 }}>(a later pull filled a past day)</span></div>
              <div className="panel" style={{ padding: 0, marginBottom: 14, overflow: 'hidden' }}>
                <table className="tbl" style={{ margin: 0 }}>
                  <thead><tr><th>Day</th><th className="num">Was</th><th className="num">Now</th><th className="num">Added</th><th>Fix</th><th>When</th></tr></thead>
                  <tbody>
                    {shownCorrections.map(c => (
                      <tr key={c.id} style={{ cursor: 'default' }}>
                        <td className="mono" style={{ fontSize: 11 }}>{fmtDay(c.day)}</td>
                        <td className="num mono" style={{ color: 'var(--ink-3)' }}>{fmt.n(c.old_tickets)}</td>
                        <td className="num mono">{fmt.n(c.new_tickets)}</td>
                        <td className="num mono" style={{ color: 'var(--accent)', fontWeight: 700 }}>+{fmt.n(c.delta)}</td>
                        <td className="mono" style={{ fontSize: 10, color: c.kind === 'gap' ? 'var(--accent)' : 'var(--ink-2)' }}>{c.kind === 'gap' ? '✦ gap' : 'top-up'}</td>
                        <td className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>{TRIGGER_META[c.trigger]?.label || c.trigger}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {corrections.length > CORRECTIONS_PREVIEW && (
                <button type="button" className="btn btn-sm btn-ghost" style={{ marginTop: -8, marginBottom: 14 }}
                  onClick={() => setAllCorrections(v => !v)}>
                  {allCorrections ? `Show the newest ${CORRECTIONS_PREVIEW}` : `Show all ${fmt.n(corrections.length)} corrections`}
                </button>
              )}
            </>
          )}

          {isLoading && days.length === 0 ? (
            <div className="muted" style={{ padding: 18, textAlign: 'center', fontSize: 11.5 }}>Loading daily series…</div>
          ) : t === 0 ? (
            <div className="muted" style={{ padding: 18, textAlign: 'center', fontSize: 11.5 }}>No tickets parsed for this production in the last 30 days.</div>
          ) : (
            <>
              {chartHd('Tickets per day · 30d', '(red = zero-volume gap · hover for daily counts)')}
              <div className="panel" style={{ padding: 12, marginBottom: 14 }}><MetricChart data={ticketsSeries} dayLabels={dayLabels} fmtAxis={v => fmt.n(v)} fmtPeak={v => Math.round(v).toLocaleString()} fmtTip={v => `${fmt.n(v)} tickets`} highlightZero/></div>

              {chartHd('Revenue per day · 30d', '(hover for daily revenue)')}
              <div className="panel" style={{ padding: 12, marginBottom: 14 }}><MetricChart data={revenueSeries} dayLabels={dayLabels} fmtAxis={v => fmt.$(v)} fmtPeak={v => fmt.$full(v)} fmtTip={v => fmt.$full(v)}/></div>

              {chartHd('Email coverage per day · 30d', `(show ${emailPct30}%${houseNote('email')})`)}
              <div className="panel" style={{ padding: 12, marginBottom: 14 }}><CoverageChart data={emailSeries} dayLabels={dayLabels} counts={emailCounts} totals={totalsSeries} avg={avgCoverage?.email}/></div>

              {chartHd('Phone coverage per day · 30d', `(show ${phonePct30}%${houseNote('phone')})`)}
              <div className="panel" style={{ padding: 12, marginBottom: 14 }}><CoverageChart data={phoneSeries} dayLabels={dayLabels} counts={phoneCounts} totals={totalsSeries} avg={avgCoverage?.phone}/></div>

              {chartHd('Address coverage per day · 30d', `(show ${addrPct30}%${houseNote('address')})`)}
              <div className="panel" style={{ padding: 12, marginBottom: 14 }}><CoverageChart data={addrSeries} dayLabels={dayLabels} counts={addrCounts} totals={totalsSeries} avg={avgCoverage?.address}/></div>

              <div className="field-lbl mb-2">Daily breakdown</div>
              <div className="panel" style={{ padding: 0, marginBottom: 14, overflow: 'hidden' }}>
                <table className="tbl" style={{ margin: 0 }}>
                  <thead>
                    <tr>
                      <th>Day</th>
                      <th className="num">Tickets</th>
                      <th className="num">Refunds</th>
                      <th className="num">Revenue</th>
                      <th className="num">Email%</th>
                      <th className="num">Phone%</th>
                      <th className="num">Address%</th>
                      <th className="num">Anon%</th>
                      <th className="num" title="Distinct households reached that day">Households</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeDesc.map(d => {
                      const addrPct = pct((Number(d.tickets) || 0) - (Number(d.missing_address_ct) || 0), d.tickets);
                      const fix = corrByDay.get(Number(d.day));
                      return (
                        <tr key={d.day} style={{ cursor: 'default' }}>
                          <td className="mono" style={{ fontSize: 11 }}>
                            {fix && <span title={`Backfill ${fix.kind === 'gap' ? 'filled' : 'topped up'}: was ${fix.old_tickets} → ${fix.new_tickets} (+${fix.delta}, ${TRIGGER_META[fix.trigger]?.label || fix.trigger})`} style={{ color: 'var(--accent)', marginRight: 4, fontWeight: 700 }}>✦</span>}
                            {fmtDay(d.day)}
                          </td>
                          <td className="num mono">{fmt.n(d.tickets)}</td>
                          <td className="num mono" style={{ color: d.refunds > 0 ? 'var(--warn)' : 'var(--ink-2)' }}>{fmt.n(d.refunds)}</td>
                          <td className="num"><MoneyCell value={(Number(d.revenue_cents) || 0) / 100}/></td>
                          <td className="num mono" style={{ color: metricColor(pct(d.tickets - d.email_ct, d.tickets), qualityPolicy.missingEmail) }}>{Math.round(pct(d.email_ct, d.tickets))}%</td>
                          <td className="num mono" style={{ color: NEUTRAL }}>{Math.round(pct(d.phone_ct, d.tickets))}%</td>
                          <td className="num mono" style={{ color: NEUTRAL }}>{Math.round(addrPct)}%</td>
                          <td className="num mono" style={{ color: metricColor(pct(d.anon_ct, d.tickets), qualityPolicy.anonymous) }}>{Math.round(pct(d.anon_ct, d.tickets))}%</td>
                          <td className="num mono">{fmt.n(d.household_reach_ct || 0)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="drawer-foot">
          <span className="muted mono" style={{ fontSize: 10, letterSpacing: '.06em' }}>Read-only · refreshed by the nightly + manual backfills</span>
          <button className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={onClose}>Close</button>
        </div>
      </ResponsiveOverlay>
    </>
  );
}

// ─── manual backfill modal ───────────────────────────────────────────────────
interface ManualBackfillModalProps {
  open: boolean;
  onClose: () => void;
  productions: Production[];
  onSubmit: (body: EnqueueBackfill) => void;
  submitting: boolean;
  submitError: unknown;
}

function ManualBackfillModal({ open, onClose, productions, onSubmit, submitting, submitError }: ManualBackfillModalProps) {
  const t0 = nyToday(); const today = new Date(nyMidnightMs(t0.y, t0.m, t0.d)); // NY-midnight, matches the DateFilter dropdowns
  // 29 calendar days back, at that day's NY midnight: subtracting 29 × 24 h lands an hour off
  // across a DST change (from 2026-11-02 it gave 10-04 01:00, and the backfill, which starts at
  // exactly `date_from`, skipped that day's first hour).
  const [range, setRange] = useState<DateFilterValue>(() => {
    const back = new Date(Date.UTC(t0.y, t0.m - 1, t0.d - 29));
    const start = new Date(nyMidnightMs(back.getUTCFullYear(), back.getUTCMonth() + 1, back.getUTCDate()));
    return { preset: 'custom', start, end: today };
  });
  const [productionId, setProductionId] = useState('');
  const required = useRequiredFeedback();

  const spanDays = Math.round((+range.end - +range.start) / DAY_MS) + 1;
  const error = (+range.start > +range.end) ? 'Start must be on or before end.'
    : (+range.end > +today) ? 'End date can’t be in the future.'
    : (spanDays > 90) ? `Range is ${spanDays} days — the maximum is 90.`
    : null;

  useEffect(() => {
    if (open) required.clearAll();
  }, [open, required.clearAll]);

  useEffect(() => {
    if (!error) required.clear('backfill-date-range');
  }, [error, required.clear]);

  if (!open) return null;

  const submit = () => {
    if (submitting) return;
    if (!required.report([
      error ? { key: 'backfill-date-range', message: error } : null,
    ])) return;
    onSubmit({ date_from: +range.start, date_to: +range.end, production_id: productionId || null });
  };

  return (
    <ResponsiveOverlay label="pipeline" kind="form" onClose={onClose} className="modal-host">
      <div className="modal-card" onClick={e => e.stopPropagation()} style={{ width: 520, maxWidth: '92vw' }}>
        <div className="modal-hd">
          <div>
            <div className="modal-title">Run manual backfill</div>
            <div className="muted" style={{ fontSize: 11.5 }}>Re-parses tickets and refills the daily stats for the selected window.</div>
          </div>
          <button className="modal-x" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <RequiredField
            fieldKey="backfill-date-range"
            message={required.messages['backfill-date-range']}
            attempt={required.attempt}
            className="field"
          >
            <label className="field-lbl">Date range <span className="muted" style={{ textTransform: 'none', letterSpacing: 0 }}>· max 90 days</span></label>
            <div style={{ marginTop: 6 }}><DateFilter value={range} onChange={setRange}/></div>
            <div className="field-hint" style={{ marginTop: 6 }}>{fmtInput(range.start)} → {fmtInput(range.end)} · {Math.max(spanDays, 0)} day{spanDays === 1 ? '' : 's'}</div>
          </RequiredField>
          <div className="field">
            <label className="field-lbl">Production <span className="muted" style={{ textTransform: 'none', letterSpacing: 0 }}>· optional</span></label>
            <div style={{ marginTop: 6 }}>
              <SearchableDropdown value={productionId} onChange={setProductionId}
                options={productions.map(s => ({ id: s.id, label: s.name }))}
                emptyOptionLabel="All productions" emptyOptionValue="" placeholder="All productions"
                searchPlaceholder="Search productions…" popWidth={460}/>
            </div>
          </div>
          {error && !required.messages['backfill-date-range'] && <div className="mono" style={{ fontSize: 11.5, color: 'var(--bad)' }}>● {error}</div>}
          {submitError ? <MutationErrorLine error={submitError}/> : null}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={submitting} onClick={submit}>{submitting ? 'Starting…' : 'Run backfill'}</button>
        </div>
      </div>
    </ResponsiveOverlay>
  );
}

// ─── runs drawer ─────────────────────────────────────────────────────────────
interface PillMeta {
  label: string;
  color: string;
}

// Keyed by string, not the status union: a status the server adds before this
// build knows it reads as its own uppercase name, never as a crash.
const REQ_STATUS_META: Partial<Record<string, PillMeta>> = {
  queued:      { label: 'QUEUED',      color: 'var(--ink-2)' },
  running:     { label: 'RUNNING',     color: 'var(--accent)' },
  completed:   { label: 'COMPLETED',   color: 'var(--good)' },
  failed:      { label: 'FAILED',      color: 'var(--bad)'  },
  // Terminal: the batch-worker died mid-run and the reaper flipped the row
  // (the window was NOT ingested; re-run manually).
  interrupted: { label: 'INTERRUPTED', color: 'var(--bad)'  },
};
function ReqPill({ status, glow }: { status: string; glow: boolean }) {
  const m = REQ_STATUS_META[status] || { label: String(status || '—').toUpperCase(), color: 'var(--ink-3)' };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-mono)', fontSize: 10.5, fontWeight: 600, letterSpacing: '.04em', color: m.color }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: m.color, boxShadow: glow ? `0 0 0 2px color-mix(in oklch, ${m.color} 25%, transparent)` : 'none' }}/>
      {m.label}
    </span>
  );
}

/** Corrections the drawer shows before "Show all"; the list itself is complete. */
const CORRECTIONS_PREVIEW = 12;

// Keyed by string for the same reason as REQ_STATUS_META.
const TRIGGER_META: Partial<Record<string, PillMeta>> = {
  refresh_48h:  { label: '48h refresh',  color: 'var(--accent)' },
  backfill_30d: { label: '30D backfill', color: 'var(--good)' },
  manual:       { label: 'manual',       color: 'var(--ink-2)' },
};
function TriggerBadge({ trigger }: { trigger: string }) {
  const m = TRIGGER_META[trigger] || { label: String(trigger || '—'), color: 'var(--ink-3)' };
  return (
    <span className="mono" style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: m.color, border: `1px solid color-mix(in oklch, ${m.color} 35%, transparent)`, borderRadius: 3, padding: '1px 5px' }}>{m.label}</span>
  );
}

// Source-freshness chip from a run's `source_lag_hours` (now − newest ticket).
function SourceChip({ lagHours }: { lagHours: number | null }) {
  const lagM = useSystemMetric('encore.source_lag_hours');
  if (lagHours == null) return null;
  const h = Number(lagHours);
  const band = lagM && !lagM.stale ? bandFor(lagM, h, 'unknown') : 'unknown';
  const stale = band === 'warn' || band === 'critical';
  const label = h < 1 ? 'src fresh' : h < 48 ? `src ${h}h` : `src ${Math.round(h / 24)}d`;
  return (
    <span className="mono" style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.04em', color: bandColor(band) }}>
      ◷ {label}{stale ? ' STALE' : band === 'unknown' ? ' · threshold unavailable' : ''}
    </span>
  );
}

// Copilot §2d — statuses whose story is finished get an Explain ghost (the
// postmortem). Running/queued runs don't: there is nothing to conclude yet.
const TERMINAL_RUN_STATUSES: ReadonlySet<string> = new Set(['completed', 'failed', 'interrupted']);

// A run's error, readable first. The words come from the run's status (structured), never
// from parsing `error_message`: the raw text is the worker's own diagnostic and stays, whole,
// under "Technical details" for support, laid out like `FailureNotice`. Owner, 2026-10-11:
// the panel used to show only the raw string in red (e.g. "encorepro_postgres is not
// configured").
interface RunCopy {
  headline: string;
  body: string;
  next: string | null;
}

const RUN_FAILURE_COPY: Partial<Record<string, RunCopy>> = {
  failed: {
    headline: 'This pull did not finish.',
    body: 'SAM Server stopped it with an error.',
    next: 'Scheduled pulls keep running on their own. Once the cause below is fixed, start the backfill again.',
  },
  interrupted: {
    headline: 'This pull was interrupted.',
    body: 'SAM Server stopped or restarted while it was running.',
    next: 'Scheduled pulls keep running on their own. Start the backfill again if you still need this window.',
  },
};
const RUN_NOTE_COPY: RunCopy = {
  headline: 'This run reported a problem.',
  body: 'The details below come from SAM Server.',
  next: null,
};

function RunFailure({ status, technical }: { status: string; technical: string }) {
  const copy = RUN_FAILURE_COPY[status] ?? RUN_NOTE_COPY;
  return (
    <div className="failure-notice" data-failure-code={`ingest_run_${status}`} style={{ fontSize: 11.5, lineHeight: 1.45, marginTop: 6, overflowWrap: 'anywhere' }}>
      <div className="failure-notice-headline" style={{ fontWeight: 600, color: 'var(--bad)' }}>{copy.headline}</div>
      <div className="failure-notice-body" style={{ marginTop: 2 }}>{copy.body}</div>
      {copy.next && <div className="failure-notice-next" style={{ marginTop: 4 }}><b>Next step:</b> {copy.next}</div>}
      <details className="failure-notice-technical" style={{ marginTop: 6 }}>
        <summary className="muted" style={{ cursor: 'pointer' }}>Technical details</summary>
        <div className="mono" style={{ fontSize: 10.5, marginTop: 4, opacity: 0.85 }}>{technical}</div>
      </details>
    </div>
  );
}

interface RunPanelProps {
  r: IngestRun;
  prod: string;
  copilotOn: boolean;
  monitorPolicy: MonitorPolicy;
}

function RunPanel({ r, prod, copilotOn, monitorPolicy }: RunPanelProps) {
  const corr = (Number(r.corrections_gaps) || 0) + (Number(r.corrections_topups) || 0);
  const done = r.status === 'completed';
  const hb = heartbeatState(r, monitorPolicy);
  const ex = useExplainPanel();
  return (
    <div className="panel" style={{ padding: 12, marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <ReqPill status={r.status} glow={hb === 'fresh'}/>
        <TriggerBadge trigger={r.trigger}/>
        {r.status === 'running' && r.progress != null && <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>{r.progress}%</span>}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <SourceChip lagHours={r.source_lag_hours}/>
          {copilotOn && TERMINAL_RUN_STATUSES.has(r.status) && (
            <CPSparkBtn label="Explain" open={ex.open} style={{ padding: '2px 7px', fontSize: 9 }}
              onClick={() => (ex.open ? ex.close() : ex.trigger(runExplainRequest(r.id)))}/>
          )}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <div className="mono" style={{ fontSize: 12, fontWeight: 600, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{prod}</div>
        <span className="muted mono" style={{ fontSize: 10 }}>{r.created_at ? fmtDateTime(Number(r.created_at)) : ''}</span>
      </div>
      <div className="muted mono" style={{ fontSize: 10, marginTop: 2 }}>{fmtDay(r.window_from)} → {fmtDay(r.window_to)}</div>
      {r.status === 'running' && (
        <div className="mono" style={{ fontSize: 10, marginTop: 4, color: hb === 'stale' || hb === 'missing' ? 'var(--warn)' : 'var(--ink-2)' }}>
          heartbeat {r.heartbeat_at != null
            ? `${fmtDateTime(Number(r.heartbeat_at))}${hb === 'stale' ? ' · STALE' : hb === 'unknown' ? ' · freshness policy unavailable' : ''}`
            : 'not received'} · progress {r.progress != null ? `${r.progress}%` : 'not reported'}
          {r.progress_note ? ` · ${r.progress_note}` : ''}
        </div>
      )}
      {done && (
        <div className="mono" style={{ fontSize: 10.5, marginTop: 5, color: 'var(--ink-2)' }}>
          in {fmt.n(Number(r.tickets_in) || 0)} · new {fmt.n(Number(r.tickets_new) || 0)} · upd {fmt.n(Number(r.tickets_updated) || 0)}
          <span className="muted"> · resolved {fmt.n(Number(r.resolved_ct) || 0)} / carried {fmt.n(Number(r.carried_ct) || 0)} · anon {fmt.n(Number(r.anonymous_ct) || 0)}</span>
        </div>
      )}
      {done && corr > 0 && (
        <div className="mono" style={{ fontSize: 10.5, marginTop: 5, color: 'var(--accent)', fontWeight: 700 }}>
          ✦ filled {fmt.n(Number(r.corrections_gaps) || 0)} gap{Number(r.corrections_gaps) === 1 ? '' : 's'}
          {Number(r.corrections_topups) > 0 ? ` + ${fmt.n(Number(r.corrections_topups))} top-up` : ''}
          {' · +'}{fmt.n(Number(r.corrections_tickets) || 0)} tickets across {fmt.n(Number(r.corrections_productions) || 0)} production{Number(r.corrections_productions) === 1 ? '' : 's'}
        </div>
      )}
      {r.error_message && <RunFailure status={r.status} technical={r.error_message}/>}
      {copilotOn && ex.open && (
        <div className="cp-dock-in-card">
          <CPPanel resp={ex.resp} state={ex.state} kind="ingest_run" error={ex.error}
            onClose={ex.close} onRegenerate={ex.regenerate} regenBusy={ex.state === 'loading'}/>
        </div>
      )}
    </div>
  );
}

/** What the runs drawer reads off the runs query: its state, not its data. */
interface RunsQueryState {
  isError: boolean;
  isPending: boolean;
  isSuccess: boolean;
  error: unknown;
  refetch: () => unknown;
}

interface RunsDrawerProps {
  runs: IngestRun[];
  runsQuery: RunsQueryState;
  productionById: Partial<Record<string, Production>>;
  monitorPolicy: MonitorPolicy;
  onClose: () => void;
}

function RunsDrawer({ runs, runsQuery, productionById, monitorPolicy, onClose }: RunsDrawerProps) {
  const copilotOn = useCopilotStatus().data?.enabled === true;
  return (
    <>

      <ResponsiveOverlay label="pipeline details" onClose={onClose} className="drawer">
        <div className="drawer-hd">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="muted mono" style={{ fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase' }}>EncorePro Pipeline</div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 800, letterSpacing: '.01em', margin: '4px 0 0', textTransform: 'uppercase' }}>Ingest runs</h2>
          </div>
          <button className="btn btn-sm btn-ghost" onClick={onClose} style={{ fontSize: 18, padding: '4px 10px' }}>×</button>
        </div>
        <div className="drawer-body">
          {/* "No runs yet" only after a successful empty answer: a failed or pending read is
              not an empty history (a run the operator just queued would read as missing). */}
          {runsQuery.isError && runs.length === 0 && <QueryErrorNote error={runsQuery.error} what="the ingest runs" onRetry={() => runsQuery.refetch()}/>}
          {runsQuery.isPending && <div className="muted" style={{ padding: 18, textAlign: 'center', fontSize: 11.5 }}>Loading runs…</div>}
          {runsQuery.isSuccess && runs.length === 0 && <div className="muted" style={{ padding: 18, textAlign: 'center', fontSize: 11.5 }}>No runs yet. Scheduled pulls appear here automatically; “Run backfill” starts a manual one.</div>}
          {runs.map(r => (
            <RunPanel key={r.id} r={r} copilotOn={copilotOn} monitorPolicy={monitorPolicy}
              prod={r.production_id ? (productionById[r.production_id]?.name || r.production_id) : 'All productions'}/>
          ))}
        </div>
        <div className="drawer-foot">
          <span className="muted mono" style={{ fontSize: 10, letterSpacing: '.06em' }}>Live · polls every 5s</span>
          <button className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={onClose}>Close</button>
        </div>
      </ResponsiveOverlay>
    </>
  );
}

// ─── screen ──────────────────────────────────────────────────────────────────
const STAGE_OPTIONS = [
  { id: 'all',      label: 'All stages' },
  { id: 'preview',  label: 'Preview' },
  { id: 'new',      label: 'New' },
  { id: 'open',     label: 'Open' },
  { id: 'open_run', label: 'Open run' },
  { id: 'hit',      label: 'Hit' },
  { id: 'critic',   label: 'Critic' },
  { id: 'touring',  label: 'Touring' },
  { id: 'closed',   label: 'Closed' },
];

interface PipelineViewProps {
  setView?: (viewId: string) => void;
  /** A production id from `?focus=`; opens that production's drawer. */
  focus?: string;
}

function PipelineView({ setView: _setView, focus }: PipelineViewProps) {
  const lagM = useSystemMetric('encore.source_lag_hours');
  const monitorPolicyQ = usePipelineMonitorPolicy();
  const monitorPolicy = monitorPolicyQ.data;
  const systemHealthQ = useSystemHealth();
  const qualityPolicy = useMemo((): QualityPolicy => {
    const byKey = new Map((systemHealthQ.data?.metrics ?? []).map(m => [m.key, m]));
    return {
      identity: byKey.get('encore.identity_coverage_pct'),
      refund: byKey.get('encore.refund_rate_pct'),
      anonymous: byKey.get('encore.anonymous_rate_pct'),
      missingEmail: byKey.get('encore.missing_email_rate_pct'),
      nullVenue: byKey.get('encore.null_venue_rate_pct'),
      nullEvent: byKey.get('encore.null_event_rate_pct'),
      missingValue: byKey.get('encore.missing_value_rate_pct'),
      missingCurrency: byKey.get('encore.missing_currency_rate_pct'),
    };
  }, [systemHealthQ.data]);
  const { data: productions = [], isLoading, isError, error, refetch } = useProductions();
  const statsQuery = usePipelineStats();
  const stats = statsQuery.data ?? [];
  // Stall verdicts (STALLED, NO PRESALES, Broken pipeline) read the stats; while they load or
  // after they fail there is no stat row to judge, so no verdict is given (Astra review,
  // 2026-10-11). The monitor policy itself still drives the running/upcoming filters.
  const stallPolicy = statsQuery.isSuccess ? monitorPolicy : null;
  const { data: venues = [] } = useVenues();
  const { data: campaigns = [] } = useCampaigns();
  const { data: roasRows = [] } = useReportingByProduction(); // L30D by default
  const canEdit = useCan('backend_edit', 'own');

  const productionById = useMemo(
    (): Partial<Record<string, Production>> => Object.fromEntries(productions.map(s => [s.id, s])),
    [productions],
  );
  const statById = useMemo(
    (): Partial<Record<string, ProductionStat>> => Object.fromEntries(stats.map(s => [s.production_id, s])),
    [stats],
  );
  const venueById = useMemo(
    (): Partial<Record<string, Venue>> => Object.fromEntries(venues.map(v => [v.id, v])),
    [venues],
  );
  const reportByProduction = useMemo(
    (): Partial<Record<string, ReportByProductionRow>> => Object.fromEntries(roasRows.map(r => [r.production_id, r])),
    [roasRows],
  );
  const venueOf = (p: Production): Venue | undefined => (p.venue_id ? venueById[p.venue_id] : undefined);
  const campaignsByProduction = useMemo(() => {
    const out = new Map<string, Campaign[]>();
    for (const c of campaigns) {
      if (!c.production_id) continue;
      const list = out.get(c.production_id);
      if (list) list.push(c);
      else out.set(c.production_id, [c]);
    }
    return out;
  }, [campaigns]);

  const [q, setQ] = useState('');
  const [qFilter, setQFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('all');
  const [venueFilter, setVenueFilter] = useState('all');
  // The drawer keeps only the production's id and reads production and stat from the live
  // queries on every render: a snapshot taken at the click kept 'No tickets parsed' when the
  // stats arrived later, and old totals after a backfill (Astra review, 2026-10-11).
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Deep-link ?focus=<production id> opens that production's drawer.
  useEffect(() => {
    if (focus && productionById[focus]) setSelectedId(focus);
  }, [focus, productionById]);
  const selectedProduction = selectedId ? productionById[selectedId] : undefined;
  const [modalOpen, setModalOpen] = useState(false);
  const [runsOpen, setRunsOpen] = useState(false);

  const runsQuery = useIngestRuns();
  const runs = runsQuery.data ?? [];
  // On failure the modal stays open and shows the server's message inline
  // (e.g. the 90-day span rejection) instead of silently spinning.
  const enqueue = useRegisteredMutation({
    volatileResources: ['pipeline.runs'],
    mutationFn: (body: EnqueueBackfill) => api.post<IngestRun>('/pipeline/runs', { body }),
    // `volatileResources` refetches the runs list; this only moves the UI. The old explicit
    // invalidation named `pipelineRuns`, which this file never imported (the file then opted
    // out of type checking, which hid it), so every successful enqueue threw and read as
    // "Request failed" (2026-10-11).
    onSuccess: () => { setModalOpen(false); setRunsOpen(true); },
  });
  const activeRuns = runs.filter(r => r.status === 'queued' || r.status === 'running').length;
  const runningHb = runs.filter(r => r.status === 'running').map(r => heartbeatState(r, monitorPolicy));
  const freshRunningRuns = runningHb.filter(s => s === 'fresh').length;
  // 'unknown' (policy not loaded yet) is counted separately — it is NOT evidence
  // of a dead worker and must not raise the amber "stale heartbeat" alarm.
  const staleRunningRuns = runningHb.filter(s => s === 'stale' || s === 'missing').length;
  const unknownRunningRuns = runningHb.filter(s => s === 'unknown').length;
  const queuedRuns = runs.filter(r => r.status === 'queued').length;
  // Newest finished pull's source-freshness, for the page-header staleness chip.
  const lastDone = runs.find(r => r.status === 'completed');
  const srcLag = lastDone ? lastDone.source_lag_hours : null;

  // Rows = ALL productions (AS IS), left-joined to their 30-day stat.
  const allRows = useMemo(
    (): PipelineRow[] => productions.map(p => ({ production: p, stat: statById[p.id] })).sort((a, b) => (a.production.name || '').localeCompare(b.production.name || '')),
    [productions, statById],
  );

  // Portfolio (house) average coverage across the MONITORED set with tickets.
  const avgCoverage = useMemo((): AvgCoverage => {
    const ms = allRows.flatMap(m => (isMonitored(m.production, monitorPolicy) && m.stat && m.stat.tickets > 0 ? [m.stat] : []));
    const T = ms.reduce((a, s) => a + (Number(s.tickets) || 0), 0);
    if (!T) return { email: null, phone: null, address: null };
    const se = ms.reduce((a, s) => a + (Number(s.email_ct) || 0), 0);
    const sp = ms.reduce((a, s) => a + (Number(s.phone_ct) || 0), 0);
    const sa = ms.reduce((a, s) => a + ((Number(s.tickets) || 0) - (Number(s.missing_address_ct) || 0)), 0);
    return { email: (se / T) * 100, phone: (sp / T) * 100, address: (sa / T) * 100 };
  }, [allRows, monitorPolicy]);

  const rows = allRows.filter(m => {
    const p = m.production, s = m.stat;
    if (stageFilter !== 'all' && p.tag !== stageFilter) return false;
    if (venueFilter !== 'all' && p.venue_id !== venueFilter) return false;
    const flags = qualityFlags(p, s, qualityPolicy, stallPolicy);
    if (qFilter === 'running' && !isRunning(p)) return false;
    if (qFilter === 'upcoming' && !(isUpcomingSoon(p, monitorPolicy) && !isRunning(p))) return false;
    if (qFilter === 'broken' && flags.stallKind == null) return false;
    if (qFilter === 'flagged' && flags.count === 0) return false;
    if (qFilter === 'clean' && (flags.count > 0 || flags.policyUnknown || !s || !s.tickets)) return false;
    if (q.trim()) {
      const needle = q.toLowerCase();
      const venueName = venueOf(p)?.name || '';
      if (!((p.name || '') + ' ' + venueName + ' ' + (p.venue_id || '') + ' ' + p.id).toLowerCase().includes(needle)) return false;
    }
    return true;
  });

  return (
    <div>
      <div className="page-hd">
        <div><h1 className="page-title">EncorePro Pipeline</h1></div>
        <PageActions>
          {srcLag != null && lagM && !lagM.stale && bandFor(lagM, Number(srcLag), 'unknown') !== 'ok' && (
            <span className="mono" data-phone="meta" title="Newest EncorePro ticket is older than ~a day — the source may not have refreshed." style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.04em', color: 'var(--bad)', alignSelf: 'center', marginRight: 4 }}>◷ source {Math.round(Number(srcLag) / 24)}d stale</span>
          )}
          {canEdit && (
            <button className="btn btn-primary" data-phone="inline" onClick={() => { enqueue.reset(); setModalOpen(true); }}><span>{Icons.plus}</span><span className="phone-long">Run backfill</span><span className="phone-short">Backfill</span></button>
          )}
          <button className="btn" onClick={() => setRunsOpen(true)}>Runs{activeRuns > 0 ? <span className="mono" style={{ marginLeft: 6, color: 'var(--accent)', fontWeight: 700 }}>● {activeRuns}</span> : null}</button>
          <button className="btn" onClick={() => downloadCsv(csvFilename('pipeline'), rows.map(m => {
            const p = m.production, s = m.stat;
            const flags = qualityFlags(p, s, qualityPolicy, stallPolicy);
            const rep = reportByProduction[p.id];
            return {
              'Production': p.name || '',
              'ID': p.id,
              'Venue': venueOf(p)?.name || p.venue_id || '',
              'Monitored': isRunning(p) ? 'running' : monitorPolicy && isUpcomingSoon(p, monitorPolicy) ? `opening ≤${monitorPolicy.upcoming_window_days}d` : 'no',
              'Quality': flags.stallKind || (s && s.tickets ? (flags.count > 0 ? `${flags.count} issues${flags.policyUnknown ? ' (partial)' : ''}` : flags.policyUnknown ? 'thresholds unavailable' : 'clean') : 'no data'),
              'Tickets 30d': s?.tickets ?? '',
              'Refunds 30d': s?.refunds ?? '',
              'Revenue 30d $': s?.revenue_cents != null ? (Number(s.revenue_cents) / 100).toFixed(2) : '',
              'Email %': s && s.tickets ? Math.round(pct(s.email_ct, s.tickets)) : '',
              'Phone %': s && s.tickets ? Math.round(pct(s.phone_ct, s.tickets)) : '',
              'Anon %': s && s.tickets ? Math.round(pct(s.anon_ct, s.tickets)) : '',
              'Households reached 30d': s?.household_reach_ct ?? '',
              'Ad spend 30d $': rep && rep.spend_cents != null ? (Number(rep.spend_cents) / 100).toFixed(2) : '',
              'ROAS 30d': rep?.roas ?? '',
              'Last active': s?.last_active_day != null ? dayYmd(Number(s.last_active_day)) : '',
            };
          }))}><span>{Icons.dl}</span>Export CSV</button>
        </PageActions>
      </div>

      <PipelineKpiStrip rows={rows} qualityPolicy={qualityPolicy} monitorPolicy={monitorPolicy} statsReady={statsQuery.isSuccess}/>

      {statsQuery.isError && (
        <div style={{ padding: '8px 22px', borderBottom: '1px solid var(--line)', background: 'color-mix(in oklch, var(--warn) 8%, transparent)' }}>
          <span className="mono" style={{ fontSize: 11, color: 'var(--warn)', fontWeight: 700 }}>
            ⚠ Pipeline stats unavailable — showing the production slate without ticket metrics. Retrying automatically.
          </span>
        </div>
      )}
      {(monitorPolicyQ.isError || systemHealthQ.isError) && (
        <div style={{ padding: '8px 22px', borderBottom: '1px solid var(--line)', background: 'color-mix(in oklch, var(--warn) 8%, transparent)' }}>
          <span className="mono" style={{ fontSize: 11, color: 'var(--warn)', fontWeight: 700 }}>
            ⚠ Monitoring policy or quality thresholds unavailable — classifications are withheld rather than inferred locally.
          </span>
        </div>
      )}

      {activeRuns > 0 && (
        <div style={{ padding: '8px 22px', borderBottom: '1px solid var(--line)', background: 'color-mix(in oklch, var(--accent) 7%, transparent)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="mono" style={{ fontSize: 11.5, color: staleRunningRuns > 0 ? 'var(--warn)' : 'var(--accent)', fontWeight: 700, letterSpacing: '.04em' }}>
            ● {unknownRunningRuns > 0
              ? `${unknownRunningRuns + freshRunningRuns} running`
              : `${freshRunningRuns} heartbeat-active`} · {queuedRuns} queued{staleRunningRuns > 0 ? ` · ${staleRunningRuns} stale heartbeat` : ''}
          </span>
          <button className="btn btn-sm btn-ghost" style={{ marginLeft: 'auto' }} onClick={() => setRunsOpen(true)}>View</button>
        </div>
      )}

      <ResponsiveFilters className="filter-bar">
        <div className="chip-input" style={{ flex: '1 1 0' }}>
          <span>{Icons.search}</span>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter productions by title, venue, or ID"/>
        </div>
        <Dropdown value={stageFilter} options={STAGE_OPTIONS} onChange={setStageFilter} label="Stage" width={170}/>
        <Dropdown value={venueFilter} options={[{ id: 'all', label: 'All venues' }, ...venues.map(v => ({ id: v.id, label: venueOptionLabel(v) }))]} onChange={setVenueFilter} label="Venue" width={190}/>
        <Dropdown value={qFilter} options={[
          { id: 'all', label: 'All productions' },
          { id: 'running', label: 'Running' },
          { id: 'upcoming', label: monitorPolicy ? `Opening ≤${monitorPolicy.upcoming_window_days}d` : 'Opening soon · policy unavailable' },
          { id: 'broken', label: 'Broken pipeline' },
          { id: 'flagged', label: 'Has issues' },
          { id: 'clean', label: 'Clean & active' },
        ]} onChange={setQFilter} label="Show" width={180}/>
      </ResponsiveFilters>

      <ScrollTable className="tbl-wrap white-tbl" label="Ingest runs">
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ minWidth: 240 }}>Production</th>
              <th style={{ minWidth: 120 }}>Tickets · 30d</th>
              <th className="num">Revenue · 30d</th>
              <th>Campaigns</th>
              <th className="num">Ad spend · 30d</th>
              <th className="num">ROAS · 30d</th>
              <th>Identity</th>
              <th>Missing refs</th>
              <th>Quality</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && rows.length === 0 && (
              <tr><td colSpan={9} className="muted" style={{ padding: 32, textAlign: 'center' }}>Loading…</td></tr>
            )}
            {!isLoading && isError && allRows.length === 0 && (
              <tr><td colSpan={9}><QueryErrorNote error={error} what="the production slate" onRetry={refetch}/></td></tr>
            )}
            {!isLoading && !isError && allRows.length === 0 && (
              <tr><td colSpan={9} className="muted" style={{ padding: 32, textAlign: 'center' }}>No productions yet. They sync in from EncorePro automatically.</td></tr>
            )}
            {!isLoading && allRows.length > 0 && rows.length === 0 && (
              <tr><td colSpan={9} className="muted" style={{ padding: 32, textAlign: 'center' }}>No productions match these filters.</td></tr>
            )}
            {rows.map(m => {
              const p = m.production;
              const s = m.stat;
              const v = venueOf(p);
              const flags = qualityFlags(p, s, qualityPolicy, stallPolicy);
              const refWorst = s && s.tickets ? Math.max(pct(s.null_venue_ct, s.tickets), pct(s.null_event_ct, s.tickets)) : 0;
              const prodCampaigns = campaignsByProduction.get(p.id) || [];
              const activeCount = prodCampaigns.filter(c => campaignKindOf(c) === 'live').length;
              const channels = [...new Set(prodCampaigns.map(c => c.channel))];
              const rep = reportByProduction[p.id];
              const spend30 = rep && rep.spend_cents != null ? Number(rep.spend_cents) / 100 : 0;
              const roas = rep ? rep.roas : null;
              const opens = startMs(p);
              const sub = s ? relDay(s.last_active_day) : (opens != null && isUpcomingSoon(p, monitorPolicy) ? 'opens ' + fmtDateShort(opens) : 'no tickets');
              return (
                <tr key={p.id} onClick={() => setSelectedId(p.id)}>
                  <td className="name" style={{ lineHeight: 1.25 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
                      <PosterChip production={p} size={32}/>
                      <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                        <div className="muted mono" style={{ fontSize: 10, letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 400, marginTop: 2 }}>
                          {v?.name || p.venue_id || '—'} · {sub}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {s && s.tickets ? (
                      <div className="mono"><b style={{ fontSize: 13 }}>{fmt.n(s.tickets)}</b><div className="muted" style={{ fontSize: 9.5 }}>{fmt.n(s.tickets - s.refunds)} sales · {fmt.n(s.refunds)} ref</div></div>
                    ) : <span className="muted">—</span>}
                  </td>
                  <td className="num">{s ? <MoneyCell value={(Number(s.revenue_cents) || 0) / 100}/> : <span className="muted">—</span>}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="mono num" style={{ fontSize: 12, fontWeight: 700, minWidth: 14, textAlign: 'right' }}>{activeCount}</span>
                      <ChannelGlyphs channels={channels}/>
                    </div>
                  </td>
                  <td className="num">{spend30 > 0 ? <MoneyCell value={spend30}/> : <span className="muted">—</span>}</td>
                  <td className="num mono">{roas != null && isFinite(roas) && roas > 0 ? <span>{roas.toFixed(2)}×</span> : <span className="muted">—</span>}</td>
                  <td><IdentityCell s={s} qualityPolicy={qualityPolicy}/></td>
                  <td>{s && s.tickets ? <span className="mono" style={{ fontSize: 11, fontWeight: 700, color: flags.refs ? 'var(--bad)' : flags.refsUnknown ? 'var(--ink-3)' : 'var(--good)' }}>{flags.refs ? `● ${Math.round(refWorst)}%${flags.refsUnknown ? ' · partial' : ''}` : flags.refsUnknown ? `${Math.round(refWorst)}% · unknown` : refWorst < 0.5 ? 'clean' : `${Math.round(refWorst)}%`}</span> : <span className="muted">—</span>}</td>
                  <td><QualityChip p={p} s={s} qualityPolicy={qualityPolicy} monitorPolicy={stallPolicy}/></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </ScrollTable>

      {selectedProduction && <PipelineDetailDrawer production={selectedProduction} venueName={venueOf(selectedProduction)?.name ?? null} stat={statById[selectedProduction.id]} avgCoverage={avgCoverage} qualityPolicy={qualityPolicy} onClose={() => setSelectedId(null)}/>}
      {runsOpen && <RunsDrawer runs={runs} runsQuery={runsQuery} productionById={productionById} monitorPolicy={monitorPolicy} onClose={() => setRunsOpen(false)}/>}
      <ManualBackfillModal open={modalOpen} onClose={() => setModalOpen(false)} productions={productions} onSubmit={body => enqueue.mutate(body)} submitting={enqueue.isPending} submitError={enqueue.error}/>
    </div>
  );
}

export { PipelineView };
// Back-compat re-export after the charts extraction (see components/charts.tsx).
export { AxisChart, MetricChart, CoverageChart };
